import { createGlobalStyle, styled } from 'styled-components';

export const GlobalStyles = createGlobalStyle`
  :root {
    --bg: #eaf2fb;
    --surface: rgba(247, 251, 255, 0.94);
    --surface-strong: #f8fbff;
    --surface-muted: #dce8f6;
    --line: #c4d4e7;
    --line-strong: rgba(30, 78, 131, 0.14);
    --text: #1a2940;
    --muted: #5b6d86;
    --accent: #2f6fb4;
    --accent-deep: #1e4e83;
    --accent-soft: #d4e4f5;
    --success: #2d6671;
    --shadow: 0 12px 28px rgba(24, 56, 95, 0.1);
    --radius-lg: 18px;
    --radius-md: 14px;
    --radius-sm: 10px;
  }

  * {
    box-sizing: border-box;
    -webkit-tap-highlight-color: transparent;
  }

  html {
    font-size: 16px;
    background: var(--bg);
  }

  body {
    margin: 0;
    min-height: 100vh;
    color: var(--text);
    font-family: "Avenir Next", "Trebuchet MS", sans-serif;
    background:
      radial-gradient(circle at top left, rgba(145, 188, 235, 0.42), transparent 34%),
      linear-gradient(180deg, #f4f9ff 0%, #e5eef8 100%);
  }

  body,
  button,
  input,
  textarea,
  select {
    font: inherit;
  }

  #root {
    min-height: 100vh;
  }

  a {
    color: inherit;
    text-decoration: none;
  }

  button {
    border: 0;
    cursor: pointer;
  }

  img {
    display: block;
    max-width: 100%;
  }
`;

export const AppShellFrame = styled.div`
  width: min(100%, 1040px);
  margin: 0 auto;
  padding: calc(env(safe-area-inset-top) + 8px) 8px
    calc(env(safe-area-inset-bottom) + 14px);
`;

export const AppShellHeaderCard = styled.header`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 12px;
  padding: 16px 18px;
  margin-bottom: 12px;
  border: 1px solid var(--line-strong);
  border-radius: var(--radius-lg);
  background: var(--surface);
  box-shadow: var(--shadow);
`;

export const AppShellBrandBlock = styled.div`
  min-width: 0;
`;

export const AppShellTitle = styled.h1`
  margin: 0;
  font-size: clamp(1.8rem, 4vw, 2.6rem);
  line-height: 1.05;
`;

export const AppShellMainContent = styled.main`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

export const TouchNavFrame = styled.nav`
  position: sticky;
  top: 0;
  z-index: 20;
  margin-bottom: 8px;
`;

export const TouchNavBar = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(90px, 1fr));
  gap: 4px;
  padding: 4px;
  border: 1px solid var(--line-strong);
  border-radius: 14px;
  background: var(--surface);
  box-shadow: var(--shadow);
  backdrop-filter: blur(14px);
`;

export const TouchNavItem = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  min-width: 0;
  min-height: 38px;
  padding: 0 4px;
  border-radius: 10px;
  background: ${({ $active }) => ($active ? 'var(--accent)' : 'transparent')};
  color: ${({ $active }) => ($active ? '#f8fbff' : 'var(--muted)')};
  font-size: 0.86rem;
  font-weight: 700;
  line-height: 1.1;
  text-align: center;
  transition: background 160ms ease, color 160ms ease, transform 160ms ease;

  &:active {
    transform: scale(0.98);
  }
`;

export const SongCardContainer = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  border: 1px solid var(--line-strong);
  border-radius: var(--radius-md);
  background: var(--surface);
  box-shadow: ${({ $compact }) => ($compact ? 'none' : 'var(--shadow)')};
  transition: transform 160ms ease, border-color 160ms ease;

  &:hover {
    transform: translateY(-2px);
    border-color: rgba(47, 111, 180, 0.3);
  }

  &:active {
    transform: scale(0.99);
  }
`;

export const SongCardContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ $compact }) => ($compact ? '0' : '6px')};
  padding: ${({ $compact }) => ($compact ? '12px 14px' : '18px 18px 14px')};
  cursor: pointer;
`;

export const SongCardMeta = styled.p`
  margin: 0;
  color: var(--accent-deep);
  font-size: 0.84rem;
  font-weight: 700;
  letter-spacing: 0.06em;
  text-transform: uppercase;
`;

export const SongCardTitle = styled.h2`
  display: ${({ $compact }) => ($compact ? 'grid' : 'block')};
  grid-template-columns: ${({ $compact }) =>
    $compact ? 'minmax(0, 1fr) auto' : 'none'};
  align-items: center;
  gap: ${({ $compact }) => ($compact ? '8px' : '0')};
  margin: 0;
  color: ${({ $compact }) => ($compact ? 'var(--accent-deep)' : 'var(--text)')};
  font-size: ${({ $compact }) => ($compact ? '1rem' : '1.32rem')};
  font-weight: ${({ $compact }) => ($compact ? 800 : 700)};
  line-height: ${({ $compact }) => ($compact ? '1.25' : '1.15')};
`;

export const SongCardTitleArtist = styled.span`
  color: var(--muted);
  font-weight: 600;
  text-align: right;
  white-space: nowrap;
`;

export const SongCardNote = styled.p`
  margin: 0;
  color: var(--muted);
  line-height: 1.5;
`;

export const SongCardActionRow = styled.div`
  display: flex;
  gap: 8px;
  position: relative;
  z-index: 1;
  padding: 0 18px 18px;
`;

export const SongCardActionButton = styled.button`
  display: inline-flex;
  flex: 1;
  align-items: center;
  justify-content: center;
  min-width: 0;
  min-height: 48px;
  padding: 0 14px;
  border-radius: 12px;
  background: ${({ $tone }) =>
    $tone === 'danger' ? '#d95c5c' : 'var(--surface-muted)'};
  color: ${({ $tone }) => ($tone === 'danger' ? '#f8fbff' : 'var(--text)')};
  font-size: 0.95rem;
  font-weight: 700;
  text-align: center;
  text-decoration: none;

  &:disabled {
    opacity: 0.72;
    cursor: default;
  }
`;

export const AccountPageLayout = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

export const AccountPageCard = styled.section`
  padding: 0;
`;

export const AccountPageSectionLabel = styled.p`
  margin: 0 0 10px;
  color: var(--accent-deep);
  font-size: 0.88rem;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
`;

export const AccountPageCardTitle = styled.h2`
  margin: 0;
  font-size: 1.6rem;
`;

export const AccountPageCardBody = styled.p`
  max-width: 40rem;
  margin: 10px 0 0;
  color: var(--muted);
  line-height: 1.6;
`;

export const AccountPagePrimaryButton = styled.button`
  min-height: 64px;
  padding: 0 18px;
  border-radius: var(--radius-md);
  background: var(--accent);
  color: #f8fbff;
  font-size: 1.05rem;
  font-weight: 700;
  box-shadow: var(--shadow);
`;

export const NotFoundPageLayout = styled.div`
  display: grid;
  min-height: 100vh;
  place-items: center;
  padding: 16px;
`;

export const NotFoundCard = styled.section`
  width: min(100%, 520px);
  padding: 22px;
  border: 1px solid var(--line-strong);
  border-radius: var(--radius-lg);
  background: var(--surface);
  box-shadow: var(--shadow);
`;

export const NotFoundTitle = styled.h1`
  margin: 0;
  font-size: 2rem;
`;

export const NotFoundBody = styled.p`
  margin: 12px 0 18px;
  color: var(--muted);
  line-height: 1.6;
`;

export const NotFoundBackLink = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 60px;
  padding: 0 16px;
  border-radius: var(--radius-md);
  background: var(--accent);
  color: #f8fbff;
  font-weight: 700;
`;

export const LoginPageLayout = styled.div`
  display: grid;
  min-height: 100vh;
  place-items: center;
  padding: 16px;
`;

export const LoginPanel = styled.div`
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 14px;
  width: min(100%, 1080px);

  @media (max-width: 900px) {
    grid-template-columns: 1fr;
  }
`;

export const LoginHeroBlock = styled.section`
  min-width: 0;
  overflow-wrap: anywhere;
  padding: 24px;
  border: 1px solid var(--line-strong);
  border-radius: var(--radius-lg);
  background: linear-gradient(180deg, rgba(248, 251, 255, 0.96), rgba(224, 236, 248, 0.98));
  box-shadow: var(--shadow);
`;

export const LoginEyebrow = styled.p`
  margin: 0 0 10px;
  color: var(--accent-deep);
  font-size: 0.92rem;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
`;

export const LoginTitle = styled.h1`
  margin: 0;
  font-size: 2.1rem;
  line-height: 1.15;
`;

export const LoginBody = styled.p`
  max-width: 34rem;
  margin: 14px 0 0;
  color: var(--muted);
  font-size: 1.06rem;
  line-height: 1.6;
`;

export const LoginFormCard = styled.form`
  min-width: 0;
  overflow-wrap: anywhere;
  display: flex;
  flex-direction: column;
  gap: 14px;
  padding: 22px;
  border: 1px solid var(--line-strong);
  border-radius: var(--radius-lg);
  background: var(--surface);
  box-shadow: var(--shadow);
`;

export const LoginFieldGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

export const LoginFieldLabel = styled.label`
  font-size: 0.95rem;
  font-weight: 700;
`;

export const LoginInput = styled.input`
  min-width: 0;
  min-height: 60px;
  padding: 0 16px;
  border: 1px solid var(--line);
  border-radius: var(--radius-md);
  background: #fdfefe;
  color: var(--text);
  font-size: 1rem;

  &:focus {
    outline: 2px solid rgba(47, 111, 180, 0.22);
    border-color: var(--accent);
  }
`;

export const LoginButtonStack = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
`;

export const LoginPrimaryButton = styled.button`
  min-height: 60px;
  padding: 0 16px;
  border-radius: var(--radius-md);
  background: var(--accent);
  color: #f8fbff;
  font-size: 1rem;
  font-weight: 700;

  &:disabled {
    opacity: 0.7;
    cursor: default;
  }
`;

export const LoginSecondaryButton = styled.button`
  min-height: 60px;
  padding: 0 16px;
  border-radius: var(--radius-md);
  background: var(--surface-muted);
  color: var(--text);
  font-size: 1rem;
  font-weight: 700;

  &:disabled {
    opacity: 0.7;
    cursor: default;
  }
`;

export const LoginErrorText = styled.p`
  margin: 0;
  color: #9f2525;
  font-weight: 700;
`;

export const LoginSmallPrint = styled.p`
  margin: 0;
  color: var(--muted);
  font-size: 0.94rem;
  line-height: 1.6;
`;

export const SongDetailLayout = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

export const SongDetailCard = styled.section`
  padding: 0;
`;

export const SongDetailMeta = styled.p`
  margin: 0 0 10px;
  color: var(--accent-deep);
  font-size: 0.88rem;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
`;

export const SongDetailTitle = styled.h2`
  margin: 0;
  font-size: clamp(2rem, 3vw, 2.8rem);
  line-height: 1.05;
`;

export const SongDetailBody = styled.p`
  max-width: 44rem;
  margin: 12px 0 0;
  color: var(--muted);
  font-size: 1.05rem;
  line-height: 1.6;
`;

export const SongDetailGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 12px;
  margin-top: 14px;

  @media (max-width: 720px) {
    grid-template-columns: 1fr;
  }
`;

export const SongDetailItem = styled.div`
  padding: 14px;
  border: 1px solid var(--line-strong);
  border-radius: var(--radius-md);
  background: var(--surface-strong);
`;

export const SongDetailLabel = styled.p`
  margin: 0 0 6px;
  color: var(--accent-deep);
  font-size: 0.82rem;
  font-weight: 700;
  letter-spacing: 0.06em;
  text-transform: uppercase;
`;

export const SongDetailValue = styled.p`
  margin: 0;
  color: var(--text);
  font-size: 1rem;
  font-weight: 600;
`;

export const SongDetailActions = styled.div`
  position: sticky;
  bottom: calc(env(safe-area-inset-bottom) + 6px);
  z-index: 10;
  display: grid;
  grid-auto-flow: column;
  grid-auto-columns: minmax(0, 1fr);
  gap: 4px;
  padding: 4px;
  border: 1px solid var(--line-strong);
  border-radius: 14px;
  background: var(--surface);
  box-shadow: var(--shadow);
  backdrop-filter: blur(14px);
`;

export const SongDetailActionLink = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  min-width: 0;
  min-height: 38px;
  padding: 0 4px;
  border-radius: 10px;
  background: ${({ $danger, $primary }) =>
    $danger ? '#b42318' : $primary ? 'var(--accent)' : 'transparent'};
  color: ${({ $danger, $primary }) =>
    $danger || $primary ? '#f8fbff' : 'var(--muted)'};
  font-size: clamp(0.68rem, 2.7vw, 0.86rem);
  font-weight: 700;
  line-height: 1.1;
  text-align: center;

  &:disabled {
    cursor: not-allowed;
    opacity: 0.68;
  }
`;

export const SongViewScreen = styled.div`
  display: flex;
  flex-direction: column;
  height: 100vh;
  height: 100dvh;
  overflow: hidden;
  padding: calc(env(safe-area-inset-top) + 6px) 6px
    calc(env(safe-area-inset-bottom) + 6px);
  background: linear-gradient(180deg, #0d1622 0%, #122133 100%);
  color: #f4f8ff;
`;

export const SongViewTopBar = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 10px;
`;

export const SongViewTopButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-height: 42px;
  padding: 0 14px;
  border: 1px solid rgba(210, 226, 245, 0.18);
  border-radius: 12px;
  background: rgba(15, 31, 49, 0.72);
  color: #f4f8ff;
  font-weight: 700;
  line-height: 1;
  text-align: center;
  white-space: nowrap;
  backdrop-filter: blur(12px);
`;

export const SongViewTopTitle = styled.h1`
  flex: 1;
  min-width: 0;
  margin: 0;
  font-size: clamp(1rem, 2.2vw, 1.35rem);
  line-height: 1.15;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

export const SongViewStage = styled.div`
  display: flex;
  flex: 1;
  min-height: 0;
  align-items: center;
  justify-content: center;
  padding: 0 0 6px;
`;

export const SongViewImage = styled.img`
  max-width: 100%;
  max-height: 100%;
  width: auto;
  height: auto;
  object-fit: contain;
  box-shadow: 0 14px 32px rgba(2, 8, 15, 0.42);
`;

export const SongViewMessage = styled.p`
  max-width: 32rem;
  margin: 0;
  color: rgba(236, 244, 255, 0.82);
  font-size: 1rem;
  line-height: 1.6;
  text-align: center;
`;

export const SongViewBottomStack = styled.div`
  display: flex;
  flex-direction: column;
  flex: 0 0 auto;
  gap: 4px;
`;

export const SongViewBottomBar = styled.div`
  display: grid;
  grid-template-columns: repeat(5, minmax(0, 1fr));
  align-items: center;
  gap: 4px;
`;

export const SongViewBottomButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-height: 34px;
  padding: 0 8px;
  border: 1px solid rgba(210, 226, 245, 0.18);
  border-radius: 9px;
  background: rgba(15, 31, 49, 0.82);
  color: #f4f8ff;
  font-size: 0.82rem;
  font-weight: 700;
  line-height: 1;
  text-align: center;
  backdrop-filter: blur(12px);

  &:disabled {
    opacity: 0.32;
    cursor: default;
  }
`;

export const SongViewBottomSpacer = styled.div`
  min-height: 34px;
`;

export const SongViewPageInfo = styled.p`
  min-width: 0;
  margin: 0;
  color: rgba(236, 244, 255, 0.82);
  font-size: 0.78rem;
  font-weight: 700;
  line-height: 1.1;
  text-align: center;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

export const AlbumsPageLayout = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

export const AlbumsSectionCard = styled.section`
  padding: 0;
`;

export const AlbumsSectionHeader = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
  margin-bottom: 12px;
`;

export const AlbumsSectionTitle = styled.h2`
  margin: 0;
  font-size: clamp(1.5rem, 3vw, 2rem);
`;

export const AlbumsSectionBody = styled.p`
  margin: 0;
  color: var(--muted);
  line-height: 1.6;
`;

export const AlbumsTopActions = styled.div`
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 8px;
  margin-bottom: 12px;

  @media (max-width: 640px) {
    grid-template-columns: 1fr;
  }
`;

export const AlbumsTopActionLink = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 52px;
  padding: 0 16px;
  border: 1px solid var(--line-strong);
  border-radius: var(--radius-md);
  background: var(--surface-strong);
  color: var(--accent-deep);
  font-size: 0.98rem;
  font-weight: 700;
  box-shadow: var(--shadow);
`;

export const AlbumsSearchInput = styled.input`
  width: 100%;
  min-height: 56px;
  padding: 0 16px;
  margin-bottom: 12px;
  border: 1px solid var(--line);
  border-radius: var(--radius-md);
  background: #fdfefe;
  color: var(--text);
  font-size: 1rem;

  &:focus {
    outline: 2px solid rgba(47, 111, 180, 0.22);
    border-color: var(--accent);
  }
`;

export const AlphaFilterBar = styled.div`
  display: grid;
  grid-template-columns: repeat(5, minmax(0, 1fr));
  gap: 4px;
  margin-bottom: 12px;

  @media (min-width: 720px) {
    grid-template-columns: repeat(10, minmax(0, 1fr));
  }
`;

export const AlphaFilterButton = styled.button`
  min-width: 0;
  min-height: 34px;
  padding: 0 4px;
  border: 1px solid
    ${({ $active }) => ($active ? 'transparent' : 'var(--line-strong)')};
  border-radius: 9px;
  background: ${({ $active }) =>
    $active ? 'var(--accent)' : 'var(--surface-strong)'};
  color: ${({ $active }) => ($active ? '#f8fbff' : 'var(--accent-deep)')};
  font-size: 0.78rem;
  font-weight: 700;
`;

export const SortControlBar = styled.div`
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 4px;
  margin-bottom: 12px;
`;

export const SortControlButton = styled.button`
  min-width: 0;
  min-height: 34px;
  padding: 0 8px;
  border: 1px solid
    ${({ $active }) => ($active ? 'transparent' : 'var(--line-strong)')};
  border-radius: 9px;
  background: ${({ $active }) =>
    $active ? 'var(--accent)' : 'var(--surface-strong)'};
  color: ${({ $active }) => ($active ? '#f8fbff' : 'var(--accent-deep)')};
  font-size: 0.82rem;
  font-weight: 700;
`;

export const AlbumsStatusText = styled.p`
  margin: 0;
  color: ${({ $error }) => ($error ? '#9f2525' : 'var(--muted)')};
  font-weight: ${({ $error }) => ($error ? 700 : 500)};
  line-height: 1.5;
`;

export const AlbumsList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

export const AlbumsCard = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  width: 100%;
  padding: 18px;
  border: 1px solid var(--line-strong);
  border-radius: var(--radius-md);
  background: var(--surface-strong);
  transition: border-color 160ms ease, transform 160ms ease;

  &:hover {
    border-color: var(--accent);
  }

  &:active {
    transform: scale(0.995);
  }
`;

export const AlbumsMeta = styled.p`
  margin: 0;
  color: var(--accent-deep);
  font-size: 0.82rem;
  font-weight: 700;
  letter-spacing: 0.06em;
  text-transform: uppercase;
`;

export const AlbumsListTitle = styled.p`
  display: grid;
  grid-template-columns: minmax(0, 1fr) auto;
  align-items: center;
  gap: 8px;
  margin: 0;
  color: var(--accent-deep);
  font-size: 1rem;
  font-weight: 800;
  line-height: 1.25;
`;

export const AlbumsListArtist = styled.span`
  color: var(--muted);
  font-weight: 600;
  text-align: right;
  white-space: nowrap;
`;

export const AlbumsViewTitle = styled.h2`
  margin: 0;
  color: var(--text);
  font-size: clamp(2rem, 3vw, 2.8rem);
  line-height: 1.05;
`;

export const AlbumsTitle = styled.h3`
  margin: 0;
  font-size: 1.35rem;
`;

export const AlbumsNote = styled.p`
  margin: 0;
  color: var(--muted);
  line-height: 1.5;
`;

export const AlbumsSongList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  margin-top: 6px;
`;

export const AlbumsSongListItem = styled.div`
  padding: 10px 12px;
  border: 1px solid var(--line-strong);
  border-radius: 12px;
  background: var(--surface);
  color: var(--text);
  font-weight: 600;
`;

export const AlbumsActions = styled.div`
  position: sticky;
  bottom: calc(env(safe-area-inset-bottom) + 6px);
  z-index: 10;
  display: grid;
  grid-auto-flow: column;
  grid-auto-columns: minmax(0, 1fr);
  gap: 4px;
  padding: 4px;
  margin-top: 12px;
  border: 1px solid var(--line-strong);
  border-radius: 14px;
  background: var(--surface);
  box-shadow: var(--shadow);
  backdrop-filter: blur(14px);
`;

export const AlbumsActionLink = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  min-width: 0;
  width: 100%;
  min-height: 38px;
  padding: 0 4px;
  border-radius: 10px;
  background: transparent;
  color: var(--muted);
  font-size: clamp(0.68rem, 2.7vw, 0.86rem);
  font-weight: 700;
  line-height: 1.1;
  text-align: center;
`;

export const AlbumsDeleteButton = styled.button`
  width: 100%;
  min-height: 38px;
  padding: 0 4px;
  border-radius: 10px;
  background: #d95c5c;
  color: #f8fbff;
  font-size: clamp(0.68rem, 2.7vw, 0.86rem);
  font-weight: 700;
  line-height: 1.1;
  text-align: center;

  &:disabled {
    opacity: 0.72;
    cursor: default;
  }
`;

export const AlbumEditorLayout = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

export const AlbumEditorFormCard = styled.form`
  display: flex;
  flex-direction: column;
  gap: 14px;
  padding: 0;
`;

export const AlbumEditorSectionHeader = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
`;

export const AlbumEditorSectionTitle = styled.h2`
  margin: 0;
  font-size: clamp(1.5rem, 3vw, 2rem);
`;

export const AlbumEditorSectionBody = styled.p`
  margin: 0;
  color: var(--muted);
  line-height: 1.6;
`;

export const AlbumEditorFieldGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 12px;

  @media (max-width: 720px) {
    grid-template-columns: 1fr;
  }
`;

export const AlbumEditorFieldGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

export const AlbumEditorFieldLabel = styled.label`
  font-weight: 700;
`;

export const AlbumEditorCheckboxLabel = styled.label`
  display: flex;
  align-items: center;
  gap: 10px;
  min-height: 56px;
  padding: 0 16px;
  border: 1px solid var(--line);
  border-radius: var(--radius-md);
  background: #fdfefe;
  color: var(--text);
  font-weight: 600;
`;

export const AlbumEditorCheckbox = styled.input`
  width: 18px;
  height: 18px;
  margin: 0;
  accent-color: var(--accent);
`;

export const AlbumEditorTextInput = styled.input`
  width: 100%;
  min-height: 56px;
  padding: 0 16px;
  border: 1px solid var(--line);
  border-radius: var(--radius-md);
  background: #fdfefe;
  color: var(--text);
  font-size: 1rem;

  &:focus {
    outline: 2px solid rgba(47, 111, 180, 0.22);
    border-color: var(--accent);
  }
`;

export const AlbumEditorStatusText = styled.p`
  margin: 0;
  color: ${({ $error }) => ($error ? '#9f2525' : 'var(--muted)')};
  font-weight: ${({ $error }) => ($error ? 700 : 500)};
  line-height: 1.5;
`;

export const AlbumEditorReadOnlySection = styled.section`
  display: flex;
  flex-direction: column;
  gap: 10px;
  padding: 16px;
  border: 1px solid var(--line-strong);
  border-radius: var(--radius-md);
  background: var(--surface-strong);
`;

export const AlbumEditorReadOnlyTitle = styled.h3`
  margin: 0;
  font-size: 1.1rem;
`;

export const AlbumEditorSongList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

export const AlbumEditorSongListItem = styled.div`
  display: grid;
  grid-template-columns: minmax(0, 1fr) auto auto;
  align-items: center;
  gap: 8px;
  padding: 8px;
  border: 1px solid var(--line-strong);
  border-radius: 12px;
  background: var(--surface);
`;

export const AlbumEditorSongListLink = styled.div`
  min-width: 0;
  padding: 4px 6px;
  color: var(--text);
  font-weight: 600;
`;

export const AlbumEditorSongListAction = styled.button`
  min-height: 36px;
  padding: 0 12px;
  border-radius: 9px;
  background: #d95c5c;
  color: #f8fbff;
  font-size: 0.86rem;
  font-weight: 700;

  &:disabled {
    opacity: 0.72;
    cursor: default;
  }
`;

export const AlbumEditorSongListControls = styled.div`
  display: flex;
  gap: 4px;
`;

export const AlbumEditorSongOrderButton = styled.button`
  min-height: 36px;
  padding: 0 8px;
  border-radius: 9px;
  background: var(--surface-muted);
  color: var(--text);
  font-size: 0.82rem;
  font-weight: 700;

  &:disabled {
    opacity: 0.4;
    cursor: default;
  }
`;

export const AlbumEditorPrimaryButton = styled.button`
  min-height: 60px;
  padding: 0 16px;
  border-radius: var(--radius-md);
  background: var(--accent);
  color: #f8fbff;
  font-size: 1rem;
  font-weight: 700;

  &:disabled {
    opacity: 0.72;
    cursor: default;
  }
`;

export const AlbumEditorActionRow = styled.div`
  position: sticky;
  bottom: calc(env(safe-area-inset-bottom) + 6px);
  z-index: 10;
  display: grid;
  grid-auto-flow: column;
  grid-auto-columns: minmax(0, 1fr);
  gap: 4px;
  padding: 4px;
  border: 1px solid var(--line-strong);
  border-radius: 14px;
  background: var(--surface);
  box-shadow: var(--shadow);
  backdrop-filter: blur(14px);
`;

export const AlbumEditorSecondaryLink = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  min-width: 0;
  min-height: 38px;
  padding: 0 4px;
  border-radius: 10px;
  background: transparent;
  color: var(--muted);
  font-size: clamp(0.68rem, 2.7vw, 0.86rem);
  font-weight: 700;
  line-height: 1.1;
  text-align: center;
`;

export const AlbumEditorDangerButton = styled.button`
  min-width: 0;
  min-height: 38px;
  padding: 0 4px;
  border-radius: 10px;
  background: #d95c5c;
  color: #f8fbff;
  font-size: clamp(0.68rem, 2.7vw, 0.86rem);
  font-weight: 700;
  line-height: 1.1;
  text-align: center;

  &:disabled {
    opacity: 0.72;
    cursor: default;
  }
`;

export const LibraryPageLayout = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

export const LibrarySectionCard = styled.section`
  padding: 0;
`;

export const LibrarySectionHeader = styled.div`
  margin-bottom: 12px;
`;

export const LibrarySectionTitle = styled.h3`
  margin: 0;
  font-size: 1.5rem;
`;

export const LibraryTopActions = styled.div`
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 8px;
  margin-bottom: 12px;

  @media (max-width: 640px) {
    grid-template-columns: 1fr;
  }
`;

export const LibraryTopActionLink = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 52px;
  padding: 0 16px;
  border: 1px solid var(--line-strong);
  border-radius: var(--radius-md);
  background: var(--surface-strong);
  color: var(--accent-deep);
  font-size: 0.98rem;
  font-weight: 700;
  box-shadow: var(--shadow);
`;

export const LibraryViewToggle = styled.div`
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 8px;
  margin-bottom: 12px;
`;

export const LibraryViewButton = styled.button`
  min-height: 52px;
  padding: 0 16px;
  border-radius: var(--radius-md);
  background: ${({ $active }) =>
    $active ? 'var(--accent)' : 'var(--surface-strong)'};
  color: ${({ $active }) => ($active ? '#f8fbff' : 'var(--text)')};
  font-size: 0.98rem;
  font-weight: 700;
  box-shadow: ${({ $active }) => ($active ? 'var(--shadow)' : 'none')};
`;

export const LibraryAlbumHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 12px;
  flex-wrap: wrap;
`;

export const LibraryBackButton = styled.button`
  min-height: 46px;
  padding: 0 14px;
  border-radius: var(--radius-md);
  background: var(--surface-muted);
  color: var(--text);
  font-weight: 700;
`;

export const LibraryAlbumTitleBlock = styled.div`
  min-width: 0;
`;

export const LibraryAlbumTitle = styled.h4`
  margin: 0;
  font-size: 1.2rem;
`;

export const LibraryAlbumMeta = styled.p`
  margin: 4px 0 0;
  color: var(--muted);
  font-size: 0.94rem;
`;

export const LibrarySearchInput = styled.input`
  width: 100%;
  min-height: 56px;
  padding: 0 16px;
  margin-bottom: 12px;
  border: 1px solid var(--line);
  border-radius: var(--radius-md);
  background: #fdfefe;
  color: var(--text);
  font-size: 1rem;

  &:focus {
    outline: 2px solid rgba(47, 111, 180, 0.22);
    border-color: var(--accent);
  }
`;

export const LibraryStatusText = styled.p`
  margin: 0 0 12px;
  color: ${({ $error }) => ($error ? '#9f2525' : 'var(--muted)')};
  font-weight: ${({ $error }) => ($error ? 700 : 500)};
  line-height: 1.5;
`;

export const LibrarySongList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

export const LibraryAlbumList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

export const LibraryAlbumCardButton = styled.button`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 6px;
  width: 100%;
  padding: 18px;
  border: 1px solid var(--line-strong);
  border-radius: var(--radius-md);
  background: var(--surface);
  box-shadow: var(--shadow);
  color: var(--text);
  text-align: left;
  transition: transform 160ms ease, border-color 160ms ease;

  &:hover {
    transform: translateY(-2px);
    border-color: rgba(47, 111, 180, 0.3);
  }

  &:active {
    transform: scale(0.99);
  }
`;

export const LibraryAlbumCardMeta = styled.p`
  margin: 0;
  color: var(--accent-deep);
  font-size: 0.84rem;
  font-weight: 700;
  letter-spacing: 0.06em;
  text-transform: uppercase;
`;

export const LibraryAlbumCardTitle = styled.h4`
  margin: 0;
  font-size: 1.28rem;
  line-height: 1.15;
`;

export const LibraryAlbumCardNote = styled.p`
  margin: 0;
  color: var(--muted);
  line-height: 1.5;
`;

export const SongEditorLayout = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

export const SongEditorFormCard = styled.form`
  display: flex;
  flex-direction: column;
  gap: 14px;
  padding: 0;
`;

export const SongEditorSectionHeader = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
`;

export const SongEditorSectionTitle = styled.h2`
  margin: 0;
  font-size: clamp(1.5rem, 3vw, 2rem);
`;

export const SongEditorSectionBody = styled.p`
  margin: 0;
  color: var(--muted);
  line-height: 1.6;
`;

export const SongEditorFieldGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 12px;

  @media (max-width: 720px) {
    grid-template-columns: 1fr;
  }
`;

export const SongEditorFieldGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

export const SongEditorFieldLabel = styled.label`
  font-weight: 700;
`;

export const SongEditorCheckboxLabel = styled.label`
  display: flex;
  align-items: center;
  gap: 10px;
  min-height: 56px;
  padding: 0 16px;
  border: 1px solid var(--line);
  border-radius: var(--radius-md);
  background: #fdfefe;
  color: var(--text);
  font-weight: 600;
`;

export const SongEditorCheckbox = styled.input`
  width: 18px;
  height: 18px;
  margin: 0;
  accent-color: var(--accent);
`;

export const SongEditorTextInput = styled.input`
  width: 100%;
  min-height: 56px;
  padding: 0 16px;
  border: 1px solid var(--line);
  border-radius: var(--radius-md);
  background: #fdfefe;
  color: var(--text);
  font-size: 1rem;

  &:focus {
    outline: 2px solid rgba(47, 111, 180, 0.22);
    border-color: var(--accent);
  }
`;

export const SongEditorUploadInput = styled.input`
  width: 100%;
  min-height: 56px;
  padding: 12px 16px;
  border: 1px solid var(--line);
  border-radius: var(--radius-md);
  background: #fdfefe;
`;

export const SongEditorHelperText = styled.p`
  margin: 0;
  color: var(--muted);
  font-size: 0.94rem;
`;

export const SongEditorPlayedCountRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 14px 16px;
  border: 1px solid var(--line-strong);
  border-radius: var(--radius-md);
  background: var(--surface-strong);

  @media (max-width: 720px) {
    flex-direction: column;
    align-items: stretch;
  }
`;

export const SongEditorPlayedCountValue = styled.p`
  margin: 0;
  color: var(--text);
  font-size: 1rem;
  font-weight: 700;
`;

export const SongEditorIncrementButton = styled.button`
  min-height: 44px;
  padding: 0 14px;
  border-radius: 12px;
  background: var(--surface-muted);
  color: var(--text);
  font-weight: 700;

  &:disabled {
    opacity: 0.72;
    cursor: default;
  }
`;

export const SongEditorStatusText = styled.p`
  margin: 0;
  color: ${({ $error }) => ($error ? '#9f2525' : 'var(--muted)')};
  font-weight: ${({ $error }) => ($error ? 700 : 500)};
  line-height: 1.5;
`;

export const SongEditorPageGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 12px;

  @media (max-width: 720px) {
    grid-template-columns: 1fr;
  }
`;

export const SongEditorPageCard = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
  padding: 14px;
  border: 1px solid var(--line-strong);
  border-radius: var(--radius-md);
  background: var(--surface-strong);
`;

export const SongEditorPagePreview = styled.img`
  width: 100%;
  border-radius: 10px;
  object-fit: contain;
`;

export const SongEditorPageActions = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
`;

export const SongEditorPageIndex = styled.p`
  margin: 0;
  color: var(--text);
  font-weight: 700;
`;

export const SongEditorPageRemoveButton = styled.button`
  min-height: 42px;
  padding: 0 12px;
  border-radius: 12px;
  background: var(--surface-muted);
  color: var(--text);
  font-weight: 700;
`;

export const SongEditorAlbumSection = styled.section`
  display: flex;
  flex-direction: column;
  gap: 10px;
  padding: 12px;
  border: 1px solid var(--line-strong);
  border-radius: var(--radius-md);
  background: var(--surface-strong);
`;

export const SongEditorAlbumSectionHeader = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
`;

export const SongEditorAlbumSectionTitle = styled.h3`
  margin: 0;
  font-size: 1.2rem;
`;

export const SongEditorAlbumSectionBody = styled.p`
  margin: 0;
  color: var(--muted);
  line-height: 1.6;
`;

export const SongEditorSelectedAlbumList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

export const SongEditorSelectedAlbumItem = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  padding: 12px 14px;
  border: 1px solid var(--line-strong);
  border-radius: 12px;
  background: var(--surface);
`;

export const SongEditorSelectedAlbumText = styled.p`
  margin: 0;
  color: var(--text);
  font-weight: 600;
`;

export const SongEditorSelectedAlbumButton = styled.button`
  min-height: 40px;
  padding: 0 12px;
  border-radius: 12px;
  background: var(--surface-muted);
  color: var(--text);
  font-weight: 700;

  &:disabled {
    opacity: 0.72;
    cursor: default;
  }
`;

export const SongEditorAlbumList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
`;

export const SongEditorAlbumCardButton = styled.button`
  display: grid;
  grid-template-columns: minmax(0, 1fr) 34px;
  align-items: center;
  gap: 8px;
  width: 100%;
  min-height: 42px;
  padding: 4px 6px 4px 12px;
  border: 1px solid var(--line-strong);
  border-radius: 10px;
  background: ${({ $selected }) =>
    $selected ? 'var(--accent-soft)' : 'var(--surface)'};
  color: var(--accent-deep);
  text-align: left;

  &:disabled {
    opacity: 0.72;
    cursor: default;
  }
`;

export const SongEditorAlbumCardMeta = styled.p`
  margin: 0;
  color: var(--accent-deep);
  font-size: 0.82rem;
  font-weight: 700;
  letter-spacing: 0.06em;
  text-transform: uppercase;
`;

export const SongEditorAlbumCardTitle = styled.h4`
  margin: 0;
  min-width: 0;
  font-size: 0.98rem;
  font-weight: 800;
  line-height: 1.2;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

export const SongEditorAlbumCardState = styled.p`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  margin: 0;
  border-radius: 999px;
  background: ${({ $selected }) =>
    $selected ? 'var(--accent)' : 'var(--surface-muted)'};
  color: ${({ $selected }) => ($selected ? '#f8fbff' : 'var(--accent-deep)')};
  font-size: 1rem;
  font-weight: 800;
  line-height: 1;
`;

export const SongEditorPrimaryButton = styled.button`
  min-width: 0;
  min-height: 38px;
  padding: 0 4px;
  border-radius: 10px;
  background: var(--accent);
  color: #f8fbff;
  font-size: clamp(0.68rem, 2.7vw, 0.86rem);
  font-weight: 700;
  line-height: 1.1;
  text-align: center;

  &:disabled {
    opacity: 0.72;
    cursor: default;
  }
`;

export const SongEditorActionRow = styled.div`
  position: sticky;
  bottom: calc(env(safe-area-inset-bottom) + 6px);
  z-index: 10;
  display: grid;
  grid-auto-flow: column;
  grid-auto-columns: minmax(0, 1fr);
  gap: 4px;
  padding: 4px;
  border: 1px solid var(--line-strong);
  border-radius: 14px;
  background: var(--surface);
  box-shadow: var(--shadow);
  backdrop-filter: blur(14px);
`;

export const SongEditorSecondaryLink = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  min-width: 0;
  min-height: 38px;
  padding: 0 4px;
  border-radius: 10px;
  background: transparent;
  color: var(--muted);
  font-size: clamp(0.68rem, 2.7vw, 0.86rem);
  font-weight: 700;
  line-height: 1.1;
  text-align: center;
`;

export const SongEditorDangerButton = styled.button`
  min-width: 0;
  min-height: 38px;
  padding: 0 4px;
  border-radius: 10px;
  background: #d95c5c;
  color: #f8fbff;
  font-size: clamp(0.68rem, 2.7vw, 0.86rem);
  font-weight: 700;
  line-height: 1.1;
  text-align: center;

  &:disabled {
    opacity: 0.72;
    cursor: default;
  }
`;
